import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ArrowRight, Check, ShieldCheck, Nfc, Clock, Building2, Gauge, FileCheck2,
  Smartphone, KeyRound, TrendingUp, BadgeCheck, AlertTriangle, ChevronRight, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { HeroBackground } from "@/components/HeroBackground";
import { Seo } from "@/components/Seo";
import { FaqSection } from "@/components/FaqSection";
import { Header, Footer, ConnectedMap } from "./Landing";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d },
});

function ProductHero({ badge, title, sub, primary }) {
  const { t } = useTranslation();
  return (
    <section className="hero-navy relative pb-20 pt-32 sm:pt-40">
      <HeroBackground />
      <div className="relative mx-auto max-w-4xl px-5 text-center">
        <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
          <span className="h-1.5 w-1.5 rounded-full bg-cyan" /> {badge}
        </motion.div>
        <motion.h1 {...fade(0.05)} className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">{title}</motion.h1>
        <motion.p {...fade(0.1)} className="mx-auto mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">{sub}</motion.p>
        <motion.div {...fade(0.15)} className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/register"><Button size="lg" data-testid="pp-primary-cta" className="w-full rounded-full bg-cyan px-7 text-white transition-transform hover:bg-cyan-dark hover:scale-[1.02] active:scale-[0.98] sm:w-auto">{primary} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          <Link to="/"><Button size="lg" variant="outline" className="w-full rounded-full border-white/20 bg-transparent px-7 text-white hover:bg-white/10 hover:text-white sm:w-auto">{t("lp.pp.backPlatform")}</Button></Link>
        </motion.div>
      </div>
    </section>
  );
}

function FeatureGrid({ items, icons }) {
  return (
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {items.map(([t, d], i) => {
        const Icon = icons[i];
        return (
          <motion.div key={t} {...fade(i * 0.05)} className="card-soft p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-navy to-cyan-dark text-white"><Icon className="h-5 w-5" /></div>
            <h3 className="mt-4 font-heading text-lg font-bold text-navy">{t}</h3>
            <p className="mt-2 text-sm text-slate-500">{d}</p>
          </motion.div>
        );
      })}
    </div>
  );
}

function Shell({ children }) {
  return <div className="bg-background"><Header />{children}<Footer /></div>;
}

/* ------------------------------------------------------------- Foreign Workforce */
export function ForeignWorkforcePage() {
  const { t } = useTranslation();
  return (
    <Shell>
      <Seo titleKey="seo.fwT" descKey="seo.fwD" path="/foreign-workforce" faq={t("lp.ppFaq.fw.items", { returnObjects: true })} breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: t("lp.foot.fw"), path: "/foreign-workforce" }]} schema={{
        "@context": "https://schema.org", "@type": "SoftwareApplication", name: "EmployGuard — Foreign Workforce Compliance",
        applicationCategory: "BusinessApplication", operatingSystem: "Web", description: t("seo.fwD"),
        offers: { "@type": "Offer", price: "49.99", priceCurrency: "EUR", category: "Subscription" },
      }} />
      <ProductHero badge={t("lp.pp.fw.badge")} title={t("lp.pp.fw.title")} sub={t("lp.pp.fw.sub")} primary={t("lp.pp.fw.primary")} />
      <section className="mx-auto max-w-7xl px-5 py-20">
        <FeatureGrid items={t("lp.pp.fw.feats", { returnObjects: true })} icons={[Gauge, ShieldCheck, FileCheck2, AlertTriangle, Smartphone, BadgeCheck]} />
        <div className="mt-10 rounded-2xl bg-slate-50 p-6 text-sm text-slate-600">
          <span className="font-semibold text-navy">{t("lp.pp.fw.know")}</span>{t("lp.pp.fw.knowRest")}
        </div>
      </section>
      <FaqSection titleKey="lp.ppFaq.fw.title" items={t("lp.ppFaq.fw.items", { returnObjects: true })} />
    </Shell>
  );
}

/* ------------------------------------------------------------- Subunternehmer */
export function SubunternehmerPage() {
  const { t } = useTranslation();
  const steps = t("lp.pp.sub.steps", { returnObjects: true });
  return (
    <Shell>
      <Seo titleKey="seo.subT" descKey="seo.subD" path="/subunternehmer" faq={t("lp.ppFaq.sub.items", { returnObjects: true })} breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: t("lp.foot.sub"), path: "/subunternehmer" }]} schema={{
        "@context": "https://schema.org", "@type": "SoftwareApplication", name: "EmployGuard Subunternehmer Compliance",
        applicationCategory: "BusinessApplication", operatingSystem: "Web", description: t("seo.subD"),
        offers: { "@type": "Offer", price: "2000", priceCurrency: "EUR", category: "Add-on (annual)" },
      }} />
      <ProductHero badge={t("lp.pp.sub.badge")} title={t("lp.pp.sub.title")} sub={t("lp.pp.sub.sub")} primary={t("lp.pp.sub.primary")} />
      <section className="mx-auto max-w-7xl px-5 py-20">
        <div className="mb-12 overflow-x-auto">
          <div className="flex min-w-max items-center gap-3">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div className="w-40 rounded-2xl border border-slate-200 bg-white p-4 premium-shadow"><div className="font-mono text-xs font-bold text-cyan-dark">0{i + 1}</div><div className="mt-1 text-sm font-semibold text-navy">{s}</div></div>
                {i < steps.length - 1 && <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />}
              </div>
            ))}
          </div>
        </div>
        <FeatureGrid items={t("lp.pp.sub.feats", { returnObjects: true })} icons={[Building2, Gauge, FileCheck2, ShieldCheck, BadgeCheck, Building2]} />
        <div className="mt-10 flex flex-col items-center gap-2">
          <Link to="/register"><Button className="rounded-full bg-cyan px-7 text-white hover:bg-cyan-dark">{t("lp.pp.sub.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          <span className="text-xs text-slate-400">{t("lp.pp.sub.addonNote")}</span>
        </div>
      </section>
      <FaqSection titleKey="lp.ppFaq.sub.title" items={t("lp.ppFaq.sub.items", { returnObjects: true })} />
    </Shell>
  );
}

/* ------------------------------------------------------------- Zeiterfassung */
export function ZeiterfassungPage() {
  const { t } = useTranslation();
  const flow = t("lp.pp.zeit.flow", { returnObjects: true });
  return (
    <Shell>
      <Seo titleKey="seo.zeitT" descKey="seo.zeitD" path="/zeiterfassung" faq={t("lp.ppFaq.zeit.items", { returnObjects: true })} breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: t("lp.foot.zeit"), path: "/zeiterfassung" }]} schema={{
        "@context": "https://schema.org", "@type": "SoftwareApplication", name: "EmployGuard Zeiterfassung",
        applicationCategory: "BusinessApplication", operatingSystem: "Web", description: t("seo.zeitD"),
        offers: { "@type": "Offer", price: "2500", priceCurrency: "EUR", category: "Add-on (annual)" },
      }} />
      <ProductHero badge={t("lp.pp.zeit.badge")} title={t("lp.pp.zeit.title")} sub={t("lp.pp.zeit.sub")} primary={t("lp.pp.zeit.primary")} />
      <section className="mx-auto max-w-7xl px-5 py-20">
        <FeatureGrid items={t("lp.pp.zeit.feats", { returnObjects: true })} icons={[Smartphone, Nfc, KeyRound, Clock, ShieldCheck, TrendingUp, MapPin]} />
        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="font-heading text-lg font-bold text-navy">{t("lp.pp.zeit.noAppTitle")}</div>
            <p className="mt-2 text-sm text-slate-500">{t("lp.pp.zeit.noAppDesc")}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              {flow.map((x, i) => (
                <div key={x} className="flex items-center gap-2"><span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">{x}</span>{i < flow.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}</div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="font-heading text-lg font-bold text-navy">{t("lp.pp.zeit.tagTitle")}</div>
            <div className="mt-3 flex items-end gap-2"><span className="font-heading text-4xl font-extrabold text-navy">€2.00</span><span className="mb-1 text-sm text-slate-400">{t("lp.pp.zeit.tagUnit")}</span></div>
            <p className="mt-3 text-sm text-slate-500">{t("lp.pp.zeit.tagDesc")}</p>
            <Link to="/register"><Button className="mt-5 rounded-full bg-navy text-white hover:bg-navy-muted">{t("lp.pp.zeit.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>
      <FaqSection titleKey="lp.ppFaq.zeit.title" items={t("lp.ppFaq.zeit.items", { returnObjects: true })} />
    </Shell>
  );
}

/* ------------------------------------------------------------- Hardware */
export function HardwarePage() {
  const { t } = useTranslation();
  const tags = t("lp.pp.hw.tags", { returnObjects: true });
  const flow = t("lp.pp.hw.flow", { returnObjects: true });
  const ids = ["EG-000184", "EG-000185"];
  return (
    <Shell>
      <Seo titleKey="seo.hwT" descKey="seo.hwD" path="/hardware" faq={t("lp.ppFaq.hw.items", { returnObjects: true })} breadcrumb={[{ name: "EmployGuard", path: "/" }, { name: t("lp.foot.hw"), path: "/hardware" }]} schema={{
        "@context": "https://schema.org", "@type": "Product", name: "EmployGuard NFC Site Tag",
        description: t("seo.hwD"), brand: { "@type": "Brand", name: "EmployGuard" },
        offers: { "@type": "Offer", price: "2.00", priceCurrency: "EUR", availability: "https://schema.org/InStock" },
      }} />
      <ProductHero badge={t("lp.pp.hw.badge")} title={t("lp.pp.hw.title")} sub={t("lp.pp.hw.sub")} primary={t("lp.pp.hw.primary")} />
      <section className="mx-auto max-w-7xl px-5 py-20">
        <div className="grid gap-6 md:grid-cols-2">
          {tags.map(([tt, d], i) => (
            <motion.div key={ids[i]} {...fade(i * 0.08)} className="card-soft flex items-center gap-5 p-6">
              <div className="w-32 shrink-0 rounded-2xl border-2 border-navy bg-white p-3 text-center">
                <div className="rounded bg-navy py-1"><span className="font-heading text-xs font-extrabold text-white">Employ</span><span className="font-heading text-xs font-extrabold text-cyan">Guard</span></div>
                <div className="my-2 flex items-center justify-center gap-1.5"><Nfc className="h-5 w-5 text-cyan-dark" /></div>
                <div className="text-[8px] tracking-[0.2em] text-slate-400">ZEITERFASSUNG</div>
                <div className="font-mono text-[10px] font-bold text-navy">{ids[i]}</div>
              </div>
              <div><div className="font-heading text-lg font-bold text-navy">{tt}</div><div className="mt-1 text-sm text-slate-500">{d}</div></div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
          {flow.map((f, i) => (
            <div key={f} className="flex items-center gap-2"><span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">{f}</span>{i < flow.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}</div>
          ))}
        </div>
        <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <span className="font-heading font-bold text-navy">€2 / tag</span>
          <span className="ml-2 text-slate-500">{t("lp.pp.hw.priceLine")}</span>
        </div>
      </section>
      <section className="bg-[#0B1120] py-20 text-white">
        <div className="mx-auto max-w-7xl px-5">
          <h2 className="text-center font-heading text-2xl font-extrabold sm:text-3xl">{t("lp.pp.hw.connTitle")}</h2>
          <div className="mt-8"><ConnectedMap /></div>
        </div>
      </section>
      <FaqSection titleKey="lp.ppFaq.hw.title" items={t("lp.ppFaq.hw.items", { returnObjects: true })} />
    </Shell>
  );
}
